import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Person from '@mui/icons-material/Person';
import SecurityUpdateGoodOutlined from '@mui/icons-material/SecurityUpdateGoodOutlined';
import { Stack, Typography } from '@mui/material';
import { Field, Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { TextField } from 'formik-mui';
import { object, string } from 'yup';

import LoadingButton from '@/components/LoadingButton';
import { validatePhoneNumber } from '@/features/persona/utils/validatePhoneNumber';
import { SessionTimeoutService } from '@/legacy/services/sessionTimeout';

import { Step } from './Step';

export type EnterPhoneNumberProps = {
  onComplete: (phoneNumber: string) => void;
  onSendSms: (phoneNumber: string) => Promise<void>;
};

export const EnterPhoneNumber = ({
  onComplete,
  onSendSms,
}: EnterPhoneNumberProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const validationSchema = useMemo(
    () =>
      object({
        phoneNumber: string()
          .required(formatMessage({ defaultMessage: 'Please enter a phone number' }))
          .test(
            'is-valid-phone-number',
            formatMessage({ defaultMessage: 'Please enter a valid phone number' }),
            validatePhoneNumber
          ),
      }),
    [formatMessage]
  );
  const onSubmit = useCallback(
    async (
      data: { phoneNumber: string },
      { setFieldError }: FormikHelpers<{ phoneNumber: string }>
    ): Promise<void> => {
      try {
        let formattedNumber = data.phoneNumber.replace(/\D/g, '');
        switch (formattedNumber.substring(0, 2)) {
          case '+1':
            break;
          case '1':
            formattedNumber = `+${formattedNumber}`;
            break;
          default:
            formattedNumber = `+1${formattedNumber}`;
            break;
        }
        await onSendSms(formattedNumber);
        onComplete(formattedNumber);
      } catch (error) {
        if (error.type === 'invalid.session') {
          await SessionTimeoutService.showTimeout();
          return;
        }
        if (error.type === 'invalid.parameter') {
          setFieldError('phoneNumber', formatMessage({ defaultMessage: 'Invalid phone number' }));
        }
        const message = error.message
          ? formatMessage(
              { defaultMessage: 'Unable to send SMS: {errorMessage}' },
              { errorMessage: error.message }
            )
          : formatMessage({ defaultMessage: 'Unable to send SMS' });
        setFieldError('phoneNumber', message);
      }
    },
    [formatMessage, onComplete, onSendSms]
  );
  const formik = useFormik({ initialValues: { phoneNumber: '' }, onSubmit, validationSchema });
  const { isSubmitting } = formik;
  return (
    <Step>
      <Stack alignItems="center" direction="row">
        <Person fontSize="large" sx={{ color: 'action.active' }} />
        <SecurityUpdateGoodOutlined sx={{ color: 'action.active' }} />
      </Stack>
      <FormikProvider value={formik}>
        <Form>
          <Stack spacing={2}>
            <Field
              component={TextField}
              label={<FormattedMessage defaultMessage="Phone Number" />}
              name="phoneNumber"
              sx={{ maxWidth: 300 }}
              type="tel"
            />
            <Typography sx={{ textAlign: 'center' }} variant="body2">
              <FormattedMessage defaultMessage="An SMS message will be sent to this number for identity verification purposes only." />
            </Typography>
            <LoadingButton fullWidth loading={isSubmitting} type="submit">
              <FormattedMessage defaultMessage="Send SMS code" />
            </LoadingButton>
          </Stack>
        </Form>
      </FormikProvider>
    </Step>
  );
};
